import React, { useState, useMemo } from 'react';
import { useWorkflow, DISTRICTS } from './WorkflowContext.tsx';
import styles from './FracSchedule.module.css';
import useTheme from '../../hooks/useTheme.ts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FiCalendar, FiClock, FiTruck, FiAlertCircle, FiCheck } from 'react-icons/fi';

// District Colors
const DISTRICT_COLORS: Record<string, { bg: string; text: string }> = {
  Northeast: { bg: '#3E76FE', text: '#ffffff' },
  Vernal: { bg: '#8CC540', text: '#ffffff' },
  Longview: { bg: '#00BCD4', text: '#ffffff' },
  Pleasanton: { bg: '#F9C844', text: '#303335' },
  Midland: { bg: '#BD9EFF', text: '#303335' },
};

// Sample fleet data for calendar
const FLEETS = [
  { id: '1000|18', district: 'Northeast', jobs: [{ start: 5, width: 30, operator: 'EOG Resources', color: '#3E76FE' }] },
  { id: '1000|20', district: 'Northeast', jobs: [{ start: 10, width: 25, operator: 'Devon Energy', color: '#3E76FE' }] },
  { id: '2100|37', district: 'Midland', jobs: [{ start: 0, width: 40, operator: 'ConocoPhillips', color: '#BD9EFF' }] },
  { id: '2100|38', district: 'Midland', jobs: [{ start: 45, width: 30, operator: 'Magnolia Oil', color: '#BD9EFF' }] },
  { id: '4000|09', district: 'Pleasanton', jobs: [{ start: 20, width: 35, operator: 'Apache Corp', color: '#F9C844' }] },
  { id: '4000|12', district: 'Vernal', jobs: [] },
  { id: '5000|04', district: 'Longview', jobs: [{ start: 15, width: 45, operator: 'SM Energy', color: '#00BCD4' }] },
  { id: '5000|08', district: 'Longview', jobs: [{ start: 0, width: 20, operator: 'Crescent Energy', color: '#00BCD4' }] },
];

interface FracScheduleProps {
  onNext?: () => void;
  isViewer?: boolean;
}

const FracSchedule: React.FC<FracScheduleProps> = ({ onNext, isViewer = false }) => {
  const { theme } = useTheme();
  const { state, updateSchedule } = useWorkflow();
  const { schedule, padData, wells } = state;
  const isDark = theme === 'dark';

  const twoDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [selectedFleet, setSelectedFleet] = useState<string | null>(schedule.fleetId || null);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>(DISTRICTS);

  // Local draft for stages/day so backspace and mid-edit typing work freely.
  // Only committed to workflow context on blur.
  const [stagesPerDayDraft, setStagesPerDayDraft] = useState<string>(
    String(schedule.expectedStagesPerDay ?? 0)
  );

  // Generate date headers for the Gantt chart
  const generateDateHeaders = () => {
    const headers: string[] = [];
    const today = new Date();
    for (let i = 0; i < 8; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i * 7);
      headers.push(date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
    }
    return headers;
  };

  const dateHeaders = useMemo(() => generateDateHeaders(), []);

  // Filter fleets by selected districts
  const filteredFleets = useMemo(() => {
    return FLEETS.filter(f => selectedDistricts.includes(f.district));
  }, [selectedDistricts]);

  // Get available fleets (no current jobs or gaps available)
  const availableFleets = useMemo(() => {
    return filteredFleets.filter(f => f.jobs.length === 0 || f.jobs.some(j => j.start + j.width < 80));
  }, [filteredFleets]);

  const handleDistrictToggle = (district: string) => {
    setSelectedDistricts(prev =>
      prev.includes(district)
        ? prev.filter(d => d !== district)
        : [...prev, district]
    );
  };

  const handleFleetSelect = (fleetId: string) => {
    const fleet = FLEETS.find(f => f.id === fleetId);
    setSelectedFleet(fleetId);
    updateSchedule({
      fleetId,
      district: fleet?.district || '',
      operator: padData.company,
    });
  };

  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      updateSchedule({ startDate: date });
      // Auto-calculate end date based on wells/stages
      if (!schedule.endDate) {
        const totalStages = wells.reduce((sum, w) => sum + w.stages, 0);
        const estimatedDays = Math.ceil(totalStages / (schedule.expectedStagesPerDay || 6));
        const endDate = new Date(date);
        endDate.setDate(endDate.getDate() + estimatedDays);
        updateSchedule({ endDate });
      }
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      updateSchedule({ endDate: date });
    }
  };

  const isValid = schedule.startDate && schedule.endDate && selectedFleet;

  // Calculate job duration
  const jobDuration = useMemo(() => {
    if (schedule.startDate && schedule.endDate) {
      const diffTime = Math.abs(schedule.endDate.getTime() - schedule.startDate.getTime());
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    }
    return 0;
  }, [schedule.startDate, schedule.endDate]);

  const totalStages = wells.reduce((sum, w) => sum + w.stages, 0);
  const estimatedStagesPerDay = jobDuration > 0 ? (totalStages / jobDuration).toFixed(1) : '0';

  return (
    <div className={`${styles.container} ${isDark ? styles.dark : ''}`}>
      <div className={styles.header}>
        <h2 className={styles.title}>Schedule Job</h2>
        <p className={styles.subtitle}>
          Select dates and assign a fleet for your job
        </p>
      </div>

      {/* Date Selection */}
      <div className={styles.dateSection}>
        <div className={styles.sectionHeader}>
          <FiCalendar className={styles.sectionIcon} />
          <span>Job Schedule</span>
        </div>

        <div className={styles.dateGrid}>
          <div className={styles.dateGroup}>
            <label>Start Date *</label>
            <DatePicker
              selected={schedule.startDate ?? null}
              onChange={handleStartDateChange}
              dateFormat="MMMM d, yyyy"
              openToDate={schedule.startDate ?? twoDaysAgo}
              className={styles.datePicker}
              placeholderText="Select start date"
              disabled={isViewer}
            />
          </div>

          <div className={styles.dateGroup}>
            <label>End Date *</label>
            <DatePicker
              selected={schedule.endDate}
              onChange={handleEndDateChange}
              dateFormat="MMMM d, yyyy"
              minDate={schedule.startDate || new Date()}
              className={styles.datePicker}
              placeholderText="Select end date"
              disabled={isViewer}
            />
          </div>

          <div className={styles.dateGroup}>
            <label>Expected Stages/Day</label>
            <input
              type="number"
              step="0.1"
              value={stagesPerDayDraft}
              onChange={e => setStagesPerDayDraft(e.target.value)}
              onBlur={() => {
                const parsed = parseFloat(stagesPerDayDraft);
                const val = isNaN(parsed) || parsed < 0 ? 0 : parsed;
                setStagesPerDayDraft(String(val));
                updateSchedule({ expectedStagesPerDay: val });
              }}
              className={styles.input}
              disabled={isViewer}
            />
          </div>
        </div>

        {/* Duration Stats */}
        {schedule.startDate && schedule.endDate && (
          <div className={styles.durationStats}>
            <div className={styles.statItem}>
              <FiClock className={styles.statIcon} />
              <div className={styles.statContent}>
                <span className={styles.statValue}>{jobDuration}</span>
                <span className={styles.statLabel}>Days</span>
              </div>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statIcon}>📊</span>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{totalStages}</span>
                <span className={styles.statLabel}>Total Stages</span>
              </div>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statIcon}>⚡</span>
              <div className={styles.statContent}>
                <span className={styles.statValue}>{estimatedStagesPerDay}</span>
                <span className={styles.statLabel}>Est. Stages/Day</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mini Gantt Calendar */}
      <div className={styles.calendarSection}>
        <div className={styles.sectionHeader}>
          <FiTruck className={styles.sectionIcon} />
          <span>Fleet Availability</span>
          <span className={styles.sectionHint}>Select an available fleet</span>
        </div>

        {/* District Filters */}
        <div className={styles.districtFilters}>
          {DISTRICTS.map(district => (
            <button
              key={district}
              className={`${styles.districtChip} ${selectedDistricts.includes(district) ? styles.active : ''}`}
              onClick={() => !isViewer && handleDistrictToggle(district)}
              disabled={isViewer}
              style={{
                backgroundColor: selectedDistricts.includes(district)
                  ? DISTRICT_COLORS[district].bg
                  : undefined,
                color: selectedDistricts.includes(district)
                  ? DISTRICT_COLORS[district].text
                  : undefined,
                borderColor: DISTRICT_COLORS[district].bg,
              }}
            >
              {district}
            </button>
          ))}
        </div>

        {/* Gantt Chart */}
        <div className={styles.ganttContainer}>
          {/* Date Headers */}
          <div className={styles.ganttHeader}>
            <div className={styles.fleetColumn}>Fleet</div>
            <div className={styles.timelineColumns}>
              {dateHeaders.map((date, i) => (
                <div key={i} className={styles.dateColumn}>{date}</div>
              ))}
            </div>
          </div>

          {/* Fleet Rows */}
          <div className={styles.ganttBody}>
            {filteredFleets.map(fleet => {
              const isSelected = selectedFleet === fleet.id;
              const isAvailable = fleet.jobs.length === 0;
              const districtColor = DISTRICT_COLORS[fleet.district];

              return (
                <div
                  key={fleet.id}
                  className={`${styles.ganttRow} ${isSelected ? styles.selected : ''} ${isAvailable ? styles.available : ''}`}
                  onClick={() => !isViewer && handleFleetSelect(fleet.id)}
                  style={{ cursor: isViewer ? 'default' : undefined }}
                >
                  <div
                    className={styles.fleetLabel}
                    style={{ borderLeftColor: districtColor.bg }}
                  >
                    <span className={styles.fleetName}>{fleet.id}</span>
                    <span
                      className={styles.districtBadge}
                      style={{
                        backgroundColor: districtColor.bg,
                        color: districtColor.text,
                      }}
                    >
                      {fleet.district}
                    </span>
                    {isSelected && (
                      <FiCheck className={styles.selectedIcon} />
                    )}
                  </div>
                  <div className={styles.timelineRow}>
                    {/* Grid lines */}
                    <div className={styles.gridLines}>
                      {dateHeaders.map((_, i) => (
                        <div key={i} className={styles.gridLine} />
                      ))}
                    </div>

                    {/* Existing Jobs */}
                    {fleet.jobs.map((job, j) => (
                      <div
                        key={j}
                        className={styles.jobBar}
                        style={{
                          left: `${job.start}%`,
                          width: `${job.width}%`,
                          backgroundColor: job.color,
                        }}
                      >
                        <span className={styles.jobLabel}>{job.operator}</span>
                      </div>
                    ))}

                    {/* Your Job Placeholder */}
                    {isSelected && schedule.startDate && (
                      <div
                        className={styles.yourJob}
                        style={{
                          left: '40%',
                          width: `${Math.min(40, jobDuration * 2)}%`,
                        }}
                      >
                        <span className={styles.yourJobLabel}>📌 {padData.padName || 'Your Job'}</span>
                      </div>
                    )}

                    {/* Available Indicator */}
                    {isAvailable && !isSelected && (
                      <div className={styles.availableIndicator}>
                        <span>Available</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Validation / Summary */}
      {!isValid ? (
        <div className={styles.validationHint}>
          <FiAlertCircle />
          <span>
            {!schedule.startDate || !schedule.endDate
              ? 'Select start and end dates'
              : 'Select a fleet to continue'}
          </span>
        </div>
      ) : (
        <div className={styles.summaryCard}>
          <div className={styles.summaryHeader}>
            <FiCheck className={styles.summaryIcon} />
            <span>Schedule Configured</span>
          </div>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Fleet</span>
              <span className={styles.summaryValue}>{selectedFleet}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Start</span>
              <span className={styles.summaryValue}>
                {schedule.startDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>End</span>
              <span className={styles.summaryValue}>
                {schedule.endDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Duration</span>
              <span className={styles.summaryValue}>{jobDuration} days</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FracSchedule;